import React, { useState, useEffect } from "react";
import GC from "@grapecity/spread-sheets";
import { SearchInput } from "./Search";

interface SpreadSearchProps {
  spread: GC.Spread.Sheets.Workbook | null;
}

interface Match {
  row: number;
  col: number;
}

export const SpreadSearch: React.FC<SpreadSearchProps> = ({ spread }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [matches, setMatches] = useState<Match[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);

  const getCellSpan = (
    sheet: GC.Spread.Sheets.Worksheet,
    row: number,
    col: number,
  ): GC.Spread.Sheets.Range | null => {
    const spans = sheet.getSpans();
    for (const span of spans) {
      if (
        row >= span.row &&
        row < span.row + span.rowCount &&
        col >= span.col &&
        col < span.col + span.colCount
      ) {
        return span;
      }
    }
    return null;
  };

  const removeCellBorder = (
    sheet: GC.Spread.Sheets.Worksheet,
    row: number,
    col: number,
  ) => {
    const span = getCellSpan(sheet, row, col);
    const r = span ? span.row : row;
    const c = span ? span.col : col;
    const rc = span ? span.rowCount : 1;
    const cc = span ? span.colCount : 1;
    const range = sheet.getRange(r, c, rc, cc);
    range.setBorder(null as any, { all: true });
  };

  /* ------------------ Clear all selection ------------------ */
  const clearSelection = () => {
    if (!spread) return;
    const sheet = spread.getActiveSheet();

    // Remove border from last highlighted match
    if (matches[currentIndex]) {
      removeCellBorder(
        sheet,
        matches[currentIndex].row,
        matches[currentIndex].col,
      );
    }

    sheet.clearSelection();
    sheet.showCell(
      0,
      0,
      GC.Spread.Sheets.VerticalPosition.center,
      GC.Spread.Sheets.HorizontalPosition.left,
    );
    setMatches([]);
    setCurrentIndex(-1);
  };

  /* ------------------ Search logic ------------------ */
  useEffect(() => {
    if (!spread) return;
    const sheet = spread.getActiveSheet();
    const normalized = searchTerm.trim().toLowerCase();
    const newMatches: Match[] = [];

    sheet.suspendPaint();

    // Clear previous selection if searchTerm empty
    if (!normalized) {
      clearSelection();
      sheet.resumePaint();
      return;
    }

    const rows = sheet.getRowCount();
    const cols = sheet.getColumnCount();
    const isHeaderRow = (sheet: GC.Spread.Sheets.Worksheet, row: number) => {
      const style = sheet.getStyle(row, 0);
      console.log("Row:", row, "Style:", style);
      return style && style.font && style.font.toLowerCase().includes("bold");
    };

    for (let r = 0; r < rows; r++) {
      if (isHeaderRow(sheet, r)) continue; // skip header row
      for (let c = 0; c < cols; c++) {
        const val = sheet.getText(r, c);
        if (val && val.toLowerCase().includes(normalized)) {
          newMatches.push({ row: r, col: c });
        }
      }
    }

    setMatches(newMatches);
    setCurrentIndex(newMatches.length ? 0 : -1);

    if (newMatches.length > 0) {
      const first = newMatches[0];
      setCellBorder(sheet, first.row, first.col);
      sheet.setActiveCell(first.row, first.col);
      sheet.showCell(first.row, first.col, 1, 1);
    }

    sheet.resumePaint();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, spread]);

  /* ------------------ Navigate matches ------------------ */
  const goToMatch = (direction: "next" | "prev") => {
    if (!spread || matches.length === 0) return;
    const sheet = spread.getActiveSheet();

    let newIndex =
      direction === "next"
        ? (currentIndex + 1) % matches.length
        : (currentIndex - 1 + matches.length) % matches.length;

    const match = matches[newIndex];

    sheet.suspendPaint();
    restoreCellBorder(
      sheet,
      matches[currentIndex]?.row ?? 0,
      matches[currentIndex]?.col ?? 0,
    );
    setCellBorder(sheet, match.row, match.col);
    sheet.setActiveCell(match.row, match.col);
    sheet.showCell(match.row, match.col, 1, 1);
    sheet.resumePaint();

    setCurrentIndex(newIndex);
  };

  const setCellBorder = (
    sheet: GC.Spread.Sheets.Worksheet,
    row: number,
    col: number,
  ) => {
    const span = getCellSpan(sheet, row, col);
    const r = span ? span.row : row;
    const c = span ? span.col : col;
    const rc = span ? span.rowCount : 1;
    const cc = span ? span.colCount : 1;
    const range = sheet.getRange(r, c, rc, cc);

    const originalBorders = {
      top: range.borderTop(),
      bottom: range.borderBottom(),
      left: range.borderLeft(),
      right: range.borderRight(),
    };
    range.tag({ originalBorders });

  };

  const restoreCellBorder = (
    sheet: GC.Spread.Sheets.Worksheet,
    row: number,
    col: number,
  ) => {
    const span = getCellSpan(sheet, row, col);
    const r = span ? span.row : row;
    const c = span ? span.col : col;
    const rc = span ? span.rowCount : 1;
    const cc = span ? span.colCount : 1;
    const range = sheet.getRange(r, c, rc, cc);

    const originalBorders = range.tag()?.originalBorders;
    if (originalBorders) {
      range.borderTop(originalBorders.top);
      range.borderBottom(originalBorders.bottom);
      range.borderLeft(originalBorders.left);
      range.borderRight(originalBorders.right);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: 360,
        padding: 12,
      }}
    >
      <SearchInput
        placeholder="Search..."
        onSearch={setSearchTerm}
        onNavigate={(dir) => goToMatch(dir)}
        showPrevNext
        value={searchTerm}
      />
    </div>
  );
};
